import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';

export interface MxmPost {
  id: number;
  orgId: number;
  title: string;
  subtitle?: string;
  cta?: string;
  mainText: string;
  logo?: string;
  photo?: string;
  background?: string;
  color1?: string;
  color2?: string;
  color3?: string;
  image1?: string;
  image2?: string;
  fontForText?: string;
  imageGeneratedTopic?: string;
  contentType?: string;
  templateName?: string;
  industry?: string;
  plannedDate?: Date | string;
  status: 'draft' | 'planned' | 'published';
  source?: string;
  scoreHint?: number;
  url?: string;
  impressions: number;
  clicks: number;
  renderImageUrl?: string;
  document?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface UpdateMxmPostDto {
  title?: string;
  subtitle?: string;
  cta?: string;
  mainText?: string;
  logo?: string;
  photo?: string;
  background?: string;
  color1?: string;
  color2?: string;
  color3?: string;
  image1?: string;
  image2?: string;
  fontForText?: string;
  imageGeneratedTopic?: string;
  contentType?: string;
  templateName?: string;
  industry?: string;
  plannedDate?: Date | string;
  status?: 'draft' | 'planned' | 'published';
  scoreHint?: number;
}

export interface QueryPostsDto {
  orgId?: number;
  customerId?: number;
  userId?: string;
  status?: 'draft' | 'planned' | 'published';
  contentType?: string;
  dateFrom?: string;
  dateTo?: string;
  month?: string; // YYYY-MM format
  suggested?: boolean;
  limit?: number;
  offset?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PostsResponse {
  data: MxmPost[];
  total: number;
}

export const useMxmPosts = (orgId?: number, enabled: boolean = true, userId?: string, autoFetch: boolean = true) => {
  const [posts, setPosts] = useState<MxmPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const fetchPosts = useCallback(
    async (query?: QueryPostsDto) => {
      if ((!orgId && !userId) || !enabled) return;

      setLoading(true);
      setError(null);
      try {
        const params: QueryPostsDto = {
          ...(orgId && { orgId }),
          ...(userId && { userId }),
          ...query,
        };
        const endpoint = userId ? '/mxm/internal/posts' : '/mxm/posts';
        const { data } = await api.get<PostsResponse>(endpoint, { params });
        setPosts(data.data || []);
        setTotal(data.total || 0);
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [orgId, userId, enabled]
  );

  const fetchSuggestedPosts = useCallback(async () => {
    await fetchPosts({ suggested: true });
  }, [fetchPosts]);

  const fetchPost = useCallback(
    async (id: number): Promise<MxmPost> => {
      try {
        const { data } = await api.get<MxmPost>(`/mxm/posts/${id}`);
        return data;
      } catch (err: unknown) {
        throw new Error(getErrorMessage(err));
      }
    },
    []
  );

  const updatePost = useCallback(
    async (id: number, dto: UpdateMxmPostDto): Promise<MxmPost> => {
      try {
        const { data } = await api.patch<MxmPost>(`/mxm/posts/${id}`, dto);
        setPosts((prev) =>
          prev.map((post) => (post.id === id ? data : post))
        );
        return data;
      } catch (err: unknown) {
        throw new Error(getErrorMessage(err));
      }
    },
    []
  );

  const removePost = useCallback(
    async (id: number): Promise<void> => {
      try {
        await api.delete(`/mxm/posts/${id}`, { data: { id } });
        setPosts((prev) => prev.filter((post) => post.id !== id));
      } catch (err: unknown) {
        throw new Error(getErrorMessage(err));
      }
    },
    []
  );

  useEffect(() => {
    if (autoFetch && enabled && (orgId || userId)) {
      fetchSuggestedPosts();
    }
  }, [autoFetch, orgId, userId, enabled, fetchSuggestedPosts]);

  return {
    posts,
    loading,
    error,
    total,
    fetchPosts,
    fetchSuggestedPosts,
    fetchPost,
    updatePost,
    removePost,
    refetch: fetchSuggestedPosts,
  };
};

